import DtoolsReviewSystemSection from "@/components/sales/dtools/DtoolsReviewSystemSection";
import ProposalCommercialSummaryCard from "@/components/proposal/ProposalCommercialSummaryCard";
import { Button } from "@/components/ui/button";
import { PROPOSAL_COPY_FONT } from "@/lib/proposalTheme";
import { formatDtoolsMoney } from "@/lib/dtoolsJsonFormat";
import { buildV2DtoolsProposalSummaryRows } from "@shared/dtoolsV2ProposalSummary";
import type {
  DtoolsV2AlternativeGroup,
  DtoolsV2CustomerProposalView,
  DtoolsV2IndependentProposalSection,
} from "@shared/dtoolsV2CustomerProposalView";
import type { DtoolsDisplaySettings } from "@shared/dtoolsDisplaySettings";
import type { ProposalCustomerPricingDisplay } from "@shared/proposalCustomerPricing";
import type { DtoolsReviewBuildContext } from "@shared/dtoolsReviewNavigation";
import type { DtoolsReviewFooter } from "@shared/dtoolsStagingTransform";
import { buildDtoolsReviewProposalTotals } from "@shared/dtoolsReviewNavigation";
import { proposalShowsRetailPriceDisclaimer } from "@shared/proposalRetailPriceDisclaimer";
import type { DtoolsOptionSectionActionsProps } from "@/components/sales/dtools/DtoolsOptionSectionActions";
import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

type SharedPanelProps = {
  reviewContext?: DtoolsReviewBuildContext;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  customerPricing?: ProposalCustomerPricingDisplay;
};

type IndependentSectionProps = SharedPanelProps & {
  section: DtoolsV2IndependentProposalSection;
  footer: DtoolsReviewFooter;
};

export function DtoolsV2IndependentSectionView({
  section,
  reviewContext,
  optionActions,
  customerPricing,
  footer,
}: IndependentSectionProps) {
  const adjustmentsTotal = buildDtoolsReviewProposalTotals({
    systems: [section.system],
    footerAdjustments: footer.adjustments,
  }).adjustmentsTotal;

  return (
    <section
      className="dtools-v2-independent-section"
      data-testid={`v2-independent-${section.sectionKey}`}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      <DtoolsReviewSystemSection
        system={section.system}
        reviewContext={reviewContext}
        optionIndex={section.hierarchyIndex}
        optionActions={optionActions}
        showRoomBreakdown={false}
        showEquipmentSort
        sectionTitle={section.title}
        hideSectionTotal
        customerSystemPage
        showRetailPriceDisclaimer={proposalShowsRetailPriceDisclaimer(customerPricing, adjustmentsTotal)}
        customerPricing={customerPricing}
      />
    </section>
  );
}

type AlternativeGroupProps = SharedPanelProps & {
  group: DtoolsV2AlternativeGroup;
  footer: DtoolsReviewFooter;
  navigationSystemKey?: string | null;
  navigationRequestToken?: number;
};

export function DtoolsV2AlternativeGroupView({
  group,
  reviewContext,
  optionActions,
  customerPricing,
  footer,
  navigationSystemKey,
  navigationRequestToken = 0,
}: AlternativeGroupProps) {
  const [selectedKey, setSelectedKey] = useState(group.options[0]?.optionKey ?? "");
  useEffect(() => {
    const target = String(navigationSystemKey ?? "").trim().toLowerCase();
    if (!target) return;
    const option = group.options.find((entry) =>
      [entry.system.systemId, entry.system.name].some(
        (value) => String(value ?? "").trim().toLowerCase() === target,
      ),
    );
    if (option) setSelectedKey(option.optionKey);
  }, [group.options, navigationRequestToken, navigationSystemKey]);
  const selected = group.options.find((opt) => opt.optionKey === selectedKey) ?? group.options[0];
  const adjustmentsTotal = buildDtoolsReviewProposalTotals({
    systems: group.options.map((opt) => opt.system),
    footerAdjustments: footer.adjustments,
  }).adjustmentsTotal;
  const showRetailPriceDisclaimer = proposalShowsRetailPriceDisclaimer(customerPricing, adjustmentsTotal);

  return (
    <section
      className="space-y-4 rounded-xl border border-[#1A2744]/10 bg-white p-4 sm:p-6"
      data-testid={`v2-alternative-group-${group.groupKey}`}
      style={{ fontFamily: PROPOSAL_COPY_FONT }}
    >
      <h2 className="text-xl font-semibold tracking-tight text-[#1A2744]">{group.groupName}</h2>

      <div className="dtools-v2-alternative-screen space-y-6">
        {group.options.length > 1 ? (
          <div className="flex flex-wrap gap-2" role="tablist" aria-label={`${group.groupName} options`}>
            {group.options.map((opt) => (
              <Button
                key={opt.optionKey}
                type="button"
                size="sm"
                variant={opt.optionKey === selectedKey ? "default" : "outline"}
                className={cn(
                  opt.optionKey === selectedKey ? "bg-[#1A2744] text-white hover:bg-[#1A2744]/90" : "",
                )}
                onClick={() => setSelectedKey(opt.optionKey)}
              >
                {opt.label}
              </Button>
            ))}
          </div>
        ) : null}
        {selected ? (
          <DtoolsReviewSystemSection
            system={selected.system}
            reviewContext={reviewContext}
            optionIndex={selected.hierarchyIndex}
            optionActions={optionActions}
            showRoomBreakdown={false}
            showEquipmentSort
            hideSectionTotal
            customerSystemPage
            showRetailPriceDisclaimer={showRetailPriceDisclaimer}
            customerPricing={customerPricing}
          />
        ) : null}
      </div>
    </section>
  );
}

type SummaryProps = {
  view: DtoolsV2CustomerProposalView;
  footer: DtoolsReviewFooter;
  displaySettings?: DtoolsDisplaySettings;
  reviewContext?: DtoolsReviewBuildContext;
  audience?: "customer" | "staff";
};

export function DtoolsV2ProposalSummaryFooter({
  view,
  footer,
  displaySettings = {},
  reviewContext,
  audience = "customer",
}: SummaryProps) {
  const { rows } = buildV2DtoolsProposalSummaryRows({
    view,
    footerAdjustments: footer.adjustments,
    displaySettings,
    reviewContext,
    formatMoney: formatDtoolsMoney,
    audience,
  });

  if (rows.length === 0) return null;

  return (
    <div data-testid="dtools-v2-proposal-summary">
      <ProposalCommercialSummaryCard title="Proposal Summary" rows={rows} className="dtools-v2-proposal-summary" />
    </div>
  );
}

type Props = {
  view: DtoolsV2CustomerProposalView;
  reviewContext?: DtoolsReviewBuildContext;
  footer: DtoolsReviewFooter;
  displaySettings?: DtoolsDisplaySettings;
  optionActions?: Omit<DtoolsOptionSectionActionsProps, "optionLabel">;
  audience?: "customer" | "staff";
  customerPricing?: ProposalCustomerPricingDisplay;
};

/** Legacy single-tab layout — retained for PDF print aggregation paths. */
export default function DtoolsV2ProposalTabPanel({
  view,
  reviewContext,
  footer,
  optionActions,
  customerPricing,
}: Props) {
  return (
    <section className="space-y-10" data-testid="dtools-v2-proposal-tab" style={{ fontFamily: PROPOSAL_COPY_FONT }}>
      {view.independentSections.map((section) => (
        <DtoolsV2IndependentSectionView
          key={section.sectionKey}
          section={section}
          footer={footer}
          reviewContext={reviewContext}
          optionActions={optionActions}
          customerPricing={customerPricing}
        />
      ))}

      {view.alternativeGroups.map((group) => (
        <DtoolsV2AlternativeGroupView
          key={group.groupKey}
          group={group}
          footer={footer}
          reviewContext={reviewContext}
          optionActions={optionActions}
          customerPricing={customerPricing}
        />
      ))}
    </section>
  );
}
